import { CircularProgress, Snackbar, Typography } from '@mui/material'
import MuiAlert, { AlertProps } from '@mui/material/Alert'
import { styled } from '@mui/system'
import * as React from 'react'

import { useSnackbarContext } from '../hooks/useSnackBar'
import { ReactComponent as IconArchive } from '../icons/archive.svg'
import { ReactComponent as IconCheck } from '../icons/check-circle-broken.svg'
import { ReactComponent as IconDownload } from '../icons/download.svg'
import { ReactComponent as IconInfo } from '../icons/info-circle.svg'
import { ReactComponent as IconUnArchive } from '../icons/logout-circle-line.svg'
import { ReactComponent as IconNoSearch } from '../icons/no-search.svg'
import { ReactComponent as IconBin } from '../icons/trash.svg'

const CustomAlert = styled(MuiAlert)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1.5),
  color: 'white',
  letterSpacing: '-0.14px',
  borderRadius: '4px',
  boxShadow: '0 2px 4px -2px rgba(16, 24, 40, 0.06), 0 4px 8px -2px rgba(16, 24, 40, 0.1)',
  '& .MuiAlert-message': {
    display: 'flex',
    alignItems: 'center',
    padding: 0,
  },
  '& .MuiAlert-action': {
    marginLeft: theme.spacing(3.5),
    marginRight: 1,
    padding: 0,
    '& button': {
      padding: 0,
    },
    '& svg': {
      width: '18px',
      height: '18px',
    },
  },
  '& svg': {
    marginRight: 1.25,
  },
  '&.success': {
    backgroundColor: theme.palette.success[600],
  },
  '&.error': {
    backgroundColor: theme.palette.error[600],
  },
  '&.remove': {
    backgroundColor: theme.palette.grey[700],
  },
  '&.process': {
    color: theme.palette.grey[700],
    backgroundColor: theme.palette.graygreen[200],
    boxShadow: '0 4px 4px -4px rgba(0, 0, 0, 0.4)',
  },
})) as typeof MuiAlert

interface IIconComponent {
  icon?: string | null
}

function IconComponent(props: IIconComponent) {
  const { icon } = props

  switch (icon) {
    case 'success': {
      return <IconCheck />
    }
    case 'process': {
      return <CircularProgress color="primary" size={20} />
    }
    case 'error': {
      return <IconInfo />
    }
    case 'remove': {
      return <IconBin />
    }
    case 'undo': {
      return <IconDownload />
    }
    case 'not-found': {
      return <IconNoSearch />
    }
    case 'archive': {
      return <IconArchive />
    }
    case 'unarchive': {
      return <IconUnArchive />
    }
    default:
      return <></>
  }
}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>((props, ref) => (
  <CustomAlert elevation={6} ref={ref} variant="filled" icon={false} {...props} />
))

export default function SimpleSnackbar() {
  const { open, action, handleSnackBarClose } = useSnackbarContext()

  return (
    <Snackbar
      open={open}
      autoHideDuration={action.duration !== undefined ? action.duration : action.type === 'process' ? 99999 : 6000}
      onClose={handleSnackBarClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
    >
      <Alert
        className={action.type}
        onClose={handleSnackBarClose}
        sx={{
          width: '100%',
          '& svg path': {
            stroke: action.type === 'remove' ? '#a3a3a3' : '',
          },
        }}
      >
        <IconComponent icon={action.icon ? action.icon : action.type} />
        <Typography variant="body2" ml={1}>
          {action.message}
        </Typography>
      </Alert>
    </Snackbar>
  )
}
